import type {FC} from 'react';
// web has no ToggleButtonGroup; its ToggleButton swallows the group's injected props
import {ButtonBase, ToggleButton, ToggleButtonGroup} from '@mui/material';
import {useInfiniteQuery, useQuery} from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Icon,
  Progress,
  ProgressTypes,
  Typography,
} from '@wellstone-solutions/web';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {
  feedbackFilterCounts,
  filterGroupsByFeedback,
  toKindOptions,
  toProgramOptions,
} from '../../adapters';
import {
  feedbackFormQueryOptions,
  indicatorsByMemberInfiniteQueryOptions,
  kindsQueryOptions,
  NEVER_ACTIVATED_SCOPE,
  NO_FIRST_ACTION_SCOPE,
  programsQueryOptions,
} from '../../queries';
import type {FeedbackFilter} from '../../types';
import {useInfiniteScroll} from '../../useInfiniteScroll';
import {useSignalFeedback} from '../../useSignalFeedback';
import type {AccessScope} from '../../useSignalFilters';
import {useSignalFilters} from '../../useSignalFilters';
import {FeedbackModal} from '../FeedbackModal';
import type {ProgramOption} from '../../adapters';
import {ProgramFilter, SignalTypeFilter} from './FilterMenus';
import {ACTIONS_CELL, KIND_CELL, ROW_GRID} from './layout';
import {SignalRow} from './SignalRow';

// The "Not using the app" group is filtered by `scope`, not `kind` — these are
// tab-exclusive scopes the API won't combine with default-feed kinds. We keep
// them out of the change-kind checkboxes and show them as their own group.
const ACCESS_KINDS: string[] = [NEVER_ACTIVATED_SCOPE, NO_FIRST_ACTION_SCOPE];

const ACCESS_SCOPE_OPTIONS: ProgramOption[] = [
  {value: NEVER_ACTIVATED_SCOPE, label: 'Never logged in'},
  {value: NO_FIRST_ACTION_SCOPE, label: 'Never started'},
];

const FEEDBACK_TABS: Array<{value: FeedbackFilter; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'pending', label: 'Not reviewed'},
  {value: 'reviewed', label: 'Reviewed'},
];

const ReviewFilterTabs: FC<{
  value: FeedbackFilter;
  counts: Record<FeedbackFilter, number>;
  onChange: (value: FeedbackFilter) => void;
}> = ({value, counts, onChange}) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    // Ignore null so the active tab can't be toggled off — one is always selected
    onChange={(_event, next: FeedbackFilter | null) => next && onChange(next)}
    aria-label="Filter by review status"
    sx={{
      '& .MuiToggleButton-root': {
        textTransform: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        fontSize: 13,
        color: 'text.secondary',
        '&.Mui-selected': {
          bgcolor: 'rgba(81, 45, 109, 0.08)',
          borderColor: 'primary.main',
          color: 'primary.main',
          fontWeight: 600,
        },
      },
    }}>
    {FEEDBACK_TABS.map((tab) => (
      <ToggleButton key={tab.value} value={tab.value}>
        {tab.label}
        <Box component="span" sx={{ml: 0.75, opacity: 0.7}}>
          {counts[tab.value]}
        </Box>
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const HeaderLabel: FC<{children?: string; sx?: Record<string, unknown>}> = ({
  children = '',
  sx = {},
}) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
      color: 'text.secondary',
      ...sx,
    }}>
    {children}
  </Typography>
);

// Clickable column header. Clicking the active column flips direction; a new
// column starts descending. The arrow shows the active direction.
const SortHeader: FC<{
  label: string;
  active: boolean;
  ascending: boolean;
  onClick: () => void;
  sx?: Record<string, unknown>;
}> = ({label, active, ascending, onClick, sx = {}}) => (
  <ButtonBase
    onClick={onClick}
    aria-label={`Sort by ${label.toLowerCase()}`}
    aria-pressed={active}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.25,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
      color: active ? 'text.primary' : 'text.secondary',
      ...sx,
    }}>
    {label}
    {active && (
      <Icon name={ascending ? 'NavArrowUp' : 'NavArrowDown'} size={14} />
    )}
  </ButtonBase>
);

const SORT = {
  signals: {desc: '-signalCount', asc: 'signalCount'},
  raised: {desc: '-computedAt', asc: 'computedAt'},
} as const;

type SortColumn = keyof typeof SORT;

export const SignalsList: FC = () => {
  const {filters, setFilters} = useSignalFilters();
  const {reviewFilter, program, kinds, scope, ordering} = filters;
  const query = useInfiniteQuery(
    indicatorsByMemberInfiniteQueryOptions({program, kinds, scope, ordering}),
  );
  // `null` (404) means no form is deployed, so the feedback buttons stay
  // hidden; a load failure is surfaced separately with a retry below.
  const formQuery = useQuery(feedbackFormQueryOptions());
  const programsQuery = useQuery(programsQueryOptions());
  const kindsQuery = useQuery(kindsQueryOptions());
  const feedback = useSignalFeedback();
  const sentinelRef = useInfiniteScroll({
    hasNextPage: query.hasNextPage,
    isFetching: query.isFetchingNextPage,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
  });

  if (query.isPending) {
    return <LoadingIndicator />;
  }

  if (query.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => query.refetch()}>
            Try again
          </Button>
        }>
        Couldn't load signals.
      </Alert>
    );
  }

  const groups = query.data.pages.flatMap((page) => page.results);

  const feedbackForm = formQuery.data ?? null;
  const active = feedback.feedbackSignalId
    ? groups
        .flatMap((group) =>
          group.indicators.map((indicator) => ({
            indicator,
            member: group.member,
          })),
        )
        .find(({indicator}) => indicator.id === feedback.feedbackSignalId)
    : undefined;
  const activeFirstName = active
    ? (active.member.displayName.trim().split(/\s+/)[0] ?? '')
    : '';

  const counts = feedbackFilterCounts(groups);
  const visibleGroups = filterGroupsByFeedback(groups, reviewFilter);
  const changeKindOptions = toKindOptions(kindsQuery.data ?? []).filter(
    (option) => !ACCESS_KINDS.includes(option.value),
  );
  const programOptions = toProgramOptions(programsQuery.data ?? []);
  const setReviewFilter = (value: FeedbackFilter) =>
    setFilters({reviewFilter: value});
  const setProgram = (value: string) => setFilters({program: value});
  // Kinds and scope are mutually exclusive: the API requires kinds to fall
  // within the active scope, so selecting one clears the other.
  const toggleKind = (slug: string) =>
    setFilters({
      scope: undefined,
      kinds: kinds.includes(slug)
        ? kinds.filter((k) => k !== slug)
        : [...kinds, slug],
    });
  const toggleScope = (value: string) =>
    setFilters({
      kinds: [],
      scope: scope === value ? undefined : (value as AccessScope),
    });
  const clearSignalType = () => setFilters({kinds: [], scope: undefined});
  const clearFilters = () =>
    setFilters({
      program: 'all',
      kinds: [],
      scope: undefined,
      reviewFilter: 'all',
    });
  const filtersActive =
    program !== 'all' ||
    kinds.length > 0 ||
    scope !== undefined ||
    reviewFilter !== 'all';

  const sortColumn: SortColumn = ordering.includes('signalCount')
    ? 'signals'
    : 'raised';
  const sortAscending = !ordering.startsWith('-');
  const toggleSort = (column: SortColumn) => {
    const isActive =
      ordering === SORT[column].desc || ordering === SORT[column].asc;
    const next = !isActive
      ? SORT[column].desc
      : ordering === SORT[column].desc
        ? SORT[column].asc
        : SORT[column].desc;
    setFilters({ordering: next});
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.25,
          mb: 2,
        }}>
        <ReviewFilterTabs
          value={reviewFilter}
          counts={counts}
          onChange={setReviewFilter}
        />
        <Box sx={{flex: '1 1 auto'}} />
        <SignalTypeFilter
          changeOptions={changeKindOptions}
          selectedKinds={kinds}
          onToggleKind={toggleKind}
          accessOptions={ACCESS_SCOPE_OPTIONS}
          selectedScope={scope}
          onToggleScope={toggleScope}
          onClear={clearSignalType}
        />
        <ProgramFilter
          options={programOptions}
          selected={program}
          onChange={setProgram}
        />
      </Box>
      {formQuery.isError && (
        <Alert
          severity="error"
          sx={{mb: 2}}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => formQuery.refetch()}>
              Try again
            </Button>
          }>
          Couldn't load the feedback form. Signal feedback is unavailable until
          it loads.
        </Alert>
      )}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            ...ROW_GRID,
            alignItems: 'center',
            py: 1,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          <HeaderLabel />
          <HeaderLabel />
          <HeaderLabel>Person</HeaderLabel>
          <HeaderLabel sx={KIND_CELL}>Signal kind</HeaderLabel>
          <SortHeader
            label="Signals"
            active={sortColumn === 'signals'}
            ascending={sortAscending}
            onClick={() => toggleSort('signals')}
          />
          <SortHeader
            label="Raised"
            active={sortColumn === 'raised'}
            ascending={sortAscending}
            onClick={() => toggleSort('raised')}
            sx={{display: {xs: 'none', md: 'inline-flex'}}}
          />
          <HeaderLabel sx={ACTIONS_CELL}>Actions</HeaderLabel>
        </Box>

        {visibleGroups.map((group, index) => (
          <SignalRow
            key={group.member.id}
            group={group}
            striped={index % 2 === 1}
            onGiveFeedback={feedbackForm ? feedback.openFeedback : undefined}
          />
        ))}

        {visibleGroups.length === 0 &&
          !query.hasNextPage &&
          (filtersActive ? (
            <Box sx={{py: 5, textAlign: 'center'}}>
              <Typography sx={{fontWeight: 600, color: 'text.secondary'}}>
                No one matches these filters
              </Typography>
              <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>
                Try a different signal type, program or review status.
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={clearFilters}
                sx={{mt: 1}}>
                Clear filters
              </Button>
            </Box>
          ) : (
            <Typography
              sx={{color: 'text.secondary', py: 4, textAlign: 'center'}}>
              No signals right now.
            </Typography>
          ))}

        {active && feedbackForm && (
          <FeedbackModal
            form={feedbackForm}
            signalHeadline={active.indicator.headline}
            firstName={activeFirstName}
            isSubmitting={feedback.submitFeedback.isPending}
            errorReason={feedback.feedbackError}
            onCancel={feedback.closeFeedback}
            onSubmit={(submission) =>
              feedback.submitFeedback.mutate(
                {indicatorId: active.indicator.id, submission},
                {
                  onSuccess: () =>
                    showAlert(
                      `Thanks for the feedback on ${activeFirstName}'s signal.`,
                      ALERT_TYPES.SUCCESS,
                    ),
                },
              )
            }
          />
        )}

        {query.hasNextPage && (
          // Native div: web's Box swallows refs, which leaves the observer blind
          <div
            ref={sentinelRef}
            data-testid="signals-sentinel"
            style={{display: 'flex', justifyContent: 'center', padding: 16}}>
            {query.isFetchingNextPage && (
              <Progress progressType={ProgressTypes.circular} size={24} />
            )}
          </div>
        )}
      </Box>
    </Box>
  );
};
